import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import minMax from "dayjs/plugin/minMax";

dayjs.extend(customParseFormat);
dayjs.extend(minMax);

const DATE_FORMATS = [
  "YYYY-MM-DD",
  "DD/MM/YYYY",
  "YYYY-MM-DDTHH:mm:ssZ",
  "YYYY-MM-DDTHH:mm:ss.SSSZ",
];

export function parseGanttDate(value) {
  if (value == null || value === "—" || value === "— —") return null;
  if (typeof value === "number" || typeof value === "boolean") return null;
  if (typeof value === "object" && !dayjs.isDayjs(value) && !(value instanceof Date)) {
    return null;
  }
  const strict = dayjs(value, DATE_FORMATS, true);
  if (strict.isValid()) return strict;
  if (dayjs.isDayjs(value) || value instanceof Date) {
    const parsed = dayjs(value);
    return parsed.isValid() ? parsed : null;
  }
  return null;
}

function isTaskDateBag(value) {
  return (
    value &&
    typeof value === "object" &&
    !Array.isArray(value) &&
    ("ExpectedDate" in value || "RealDate" in value || "UpdatedDate" in value)
  );
}

function pushActual(realRaw, updatedRaw, into) {
  const real = parseGanttDate(realRaw);
  if (real) {
    into.push(real);
    return;
  }
  const updated = parseGanttDate(updatedRaw);
  if (updated) into.push(updated);
}

/**
 * Expected vs Real/Updated for the collapsed Gantt row.
 * Colored series prefers Real; falls back to Updated (never Expected).
 */
export function collectGanttBarDates(row) {
  const expected = [];
  const actual = [];

  if (row?.ID_MUESTRA && Array.isArray(row.TaskStatus)) {
    row.TaskStatus.forEach((task) => {
      const exp = parseGanttDate(task?.expected);
      if (exp) expected.push(exp);
      pushActual(task?.real, task?.updated, actual);
    });
  } else {
    Object.values(row || {}).forEach((value) => {
      if (!isTaskDateBag(value)) return;
      const exp = parseGanttDate(value.ExpectedDate);
      if (exp) expected.push(exp);
      pushActual(value.RealDate, value.UpdatedDate, actual);
    });
  }

  (row?.logistics || []).forEach((entry) => {
    Object.values(entry || {}).forEach((value) => {
      const date = parseGanttDate(value);
      if (date) actual.push(date);
    });
  });

  if (!expected.length && !actual.length) {
    const accepted = parseGanttDate(row?.FechaAceptacion);
    if (accepted) actual.push(accepted);
  }

  return { expected, actual };
}

export function dateExtent(dates) {
  const valid = (dates || []).filter((d) => d?.isValid?.());
  if (!valid.length) return null;
  return { start: dayjs.min(valid), end: dayjs.max(valid) };
}

export function clampTime(time, startMin, endMax) {
  return Math.min(Math.max(time, startMin), endMax);
}

const MIN_BAR_PX = 10;

export function extentToLineXs(extent, scaleX, startMin, endMax) {
  if (!extent?.start?.isValid?.() || !extent?.end?.isValid?.()) return null;
  const x1 = scaleX(clampTime(extent.start.valueOf(), startMin, endMax));
  let x2 = scaleX(clampTime(extent.end.valueOf(), startMin, endMax));
  if (Math.abs(x2 - x1) < MIN_BAR_PX) {
    x2 = x1 + MIN_BAR_PX;
  }
  return { x1, x2 };
}
